import type { FamilyEvent } from '@/features/guardian/family-events';

/** Unscaled height of one hour row; callers wrap it in `scale()`. */
export const TIMELINE_HOUR_HEIGHT = 64;
/** Unscaled width of the hour-label gutter on the left. */
export const TIMELINE_GUTTER_WIDTH = 64;
/** Minimum rendered height that still fits a compact card's two large-text rows. */
export const TIMELINE_COMPACT_EVENT_MIN_HEIGHT = 44;
export const MINUTES_PER_DAY = 24 * 60;

/** 0..24 — the trailing 24 draws the closing midnight line at the bottom of the grid. */
export const TIMELINE_HOURS = Array.from({ length: 25 }, (_, hour) => hour);
/** A crowded overlap cluster reserves four event columns and one overflow column. */
export const MAX_VISIBLE_EVENTS_IN_OVERFLOW_CLUSTER = 4;
const MIN_COMPACT_VISUAL_DURATION_MINUTES = Math.ceil(
  (TIMELINE_COMPACT_EVENT_MIN_HEIGHT / TIMELINE_HOUR_HEIGHT) * 60,
);

/** An event placed in the day grid: which column it takes, out of how many. */
export type PositionedDayEvent = {
  kind: 'event';
  event: FamilyEvent;
  /** 0-based column within its overlap cluster. */
  columnIndex: number;
  /** Total columns in that cluster — the block's width is `1 / columnCount`. */
  columnCount: number;
};

export type PositionedDayOverflow = {
  kind: 'overflow';
  id: string;
  hiddenCount: number;
  startMinutes: number;
  endMinutes: number;
  columnIndex: number;
  columnCount: number;
};

export type PositionedDayItem = PositionedDayEvent | PositionedDayOverflow;

/** Minutes elapsed since midnight for `date`. */
export function getMinutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

/** 12-hour label with an uppercase meridiem, e.g. `8:00 AM` (see AGENTS.md). */
export function formatHourLabel(hour: number): string {
  if (hour === 0) return '0:00 AM';
  if (hour === 24) return '12:00 AM';
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:00 ${meridiem}`;
}

/**
 * Place a day's events side by side when their times overlap.
 *
 * Events are swept in start order and grouped into clusters of mutually overlapping
 * blocks; within a cluster each event takes the first column that has already ended, so
 * a cluster of N genuinely-overlapping events splits the width into N columns. Events
 * that merely touch (one ends exactly when the next starts) stay full width.
 */
export function layoutDayEvents(events: FamilyEvent[]): PositionedDayItem[] {
  const sorted = [...events].sort(
    (first, second) =>
      first.startMinutes - second.startMinutes || second.endMinutes - first.endMinutes,
  );

  const positioned: PositionedDayItem[] = [];
  // Events in the cluster being built, plus the end time of each open column.
  let cluster: PositionedDayEvent[] = [];
  let columnEnds: number[] = [];

  const closeCluster = () => {
    const columnCount = columnEnds.length;
    if (cluster.length > MAX_VISIBLE_EVENTS_IN_OVERFLOW_CLUSTER + 1) {
      const visibleEvents = cluster.slice(0, MAX_VISIBLE_EVENTS_IN_OVERFLOW_CLUSTER);
      const hiddenEvents = cluster.slice(MAX_VISIBLE_EVENTS_IN_OVERFLOW_CLUSTER);
      const overflowColumnCount = MAX_VISIBLE_EVENTS_IN_OVERFLOW_CLUSTER + 1;

      visibleEvents.forEach((item, columnIndex) =>
        positioned.push({ ...item, columnIndex, columnCount: overflowColumnCount }),
      );
      positioned.push({
        kind: 'overflow',
        id: `overflow-${cluster[0].event.id}`,
        hiddenCount: hiddenEvents.length,
        startMinutes: Math.min(...hiddenEvents.map(item => item.event.startMinutes)),
        endMinutes: Math.max(...hiddenEvents.map(item => item.event.endMinutes)),
        columnIndex: MAX_VISIBLE_EVENTS_IN_OVERFLOW_CLUSTER,
        columnCount: overflowColumnCount,
      });
    } else {
      cluster.forEach(item => positioned.push({ ...item, columnCount }));
    }
    cluster = [];
    columnEnds = [];
  };

  sorted.forEach(event => {
    const clusterEnd = columnEnds.length > 0 ? Math.max(...columnEnds) : -1;
    // Nothing in the cluster is still running → the previous cluster is complete.
    if (cluster.length > 0 && event.startMinutes >= clusterEnd) closeCluster();

    const visualEndMinutes = Math.max(
      event.endMinutes,
      event.startMinutes + MIN_COMPACT_VISUAL_DURATION_MINUTES,
    );
    let columnIndex = columnEnds.findIndex(end => end <= event.startMinutes);
    if (columnIndex === -1) {
      columnIndex = columnEnds.length;
      columnEnds.push(visualEndMinutes);
    } else {
      columnEnds[columnIndex] = visualEndMinutes;
    }

    cluster.push({ kind: 'event', event, columnIndex, columnCount: 1 });
  });

  if (cluster.length > 0) closeCluster();

  return positioned;
}
